import * as THREE from 'three';
import type { SolidBodyDisplayMaterialRecipe } from '../../material/solid-body-display-material-recipe';
import { ImportedModelObjectMetadataService } from '../metadata';

/**
 * 判断对象是否为特征结果体网格。
 *
 * 说明：此处使用运行时标记探测，避免从 mesh/feature 静态导入引发循环依赖。
 *
 * @param object 待判断对象。
 * @returns 命中特征结果体时返回 `true`。
 */
function isFeatureResultMeshObject(object: THREE.Object3D): boolean {
  return Reflect.get(object, 'isFeatureResultMesh') === true;
}

/**
 * 判断对象是否为特征拓扑代理（面/边/点）。
 *
 * @param object 待判断对象。
 * @returns 命中拓扑代理时返回 `true`。
 */
function isFeatureTopologyProxyObject(object: THREE.Object3D): boolean {
  return Reflect.get(object, 'isFeatureFace') === true
    || Reflect.get(object, 'isFeatureLine') === true
    || Reflect.get(object, 'isFeaturePoint') === true;
}
/**
 * 判断对象是否为导入模型子树中的运行时拓扑代理。
 *
 * @param object 待判断对象。
 * @returns 命中导入 Face / Line / Point 代理时返回 `true`。
 */
function isImportedModelTopologyProxyObject(object: THREE.Object3D): boolean {
  const isTopologyProxyObject = Reflect.get(object, 'isFeatureFace') === true
    || Reflect.get(object, 'isFeatureLine') === true
    || Reflect.get(object, 'isFeaturePoint') === true;
  if (!isTopologyProxyObject) {
    return false;
  }

  return ImportedModelObjectMetadataService.isImportedModelObject(object);
}

//------ 模型显示模式 开始------
/**
 * 判断 mesh 是否为可用于显示模式覆盖的导入实体网格。
 *
 * @param object 场景对象。
 * @returns 满足显示模式覆盖条件时返回 `true`。
 */
export function isImportedSolidBodyDisplayMesh(object: THREE.Object3D): object is THREE.Mesh {
  if (!(object instanceof THREE.Mesh)) {
    return false;
  }
  if (isFeatureResultMeshObject(object)) {
    return false;
  }
  if (object.userData?.skipSelection || object.userData?.isImportedModelOverlay) {
    return false;
  }
  if (object.userData?.isSelectionHighlight) {
    return false;
  }
  if (object.name.startsWith('imported-topology-host:')) {
    return false;
  }
  if (isImportedModelTopologyProxyObject(object)) {
    return false;
  }
  if (isFeatureTopologyProxyObject(object)) {
    return false;
  }

  return ImportedModelObjectMetadataService.isImportedModelObject(object);
}
//------ 模型显示模式 结束------

/**
 * 判断 mesh 是否为当前可见的导入模型实体网格。
 *
 * @param object 场景对象。
 * @returns 满足可见采样条件时返回 `true`。
 */
function isImportedVisibleSolidBodyMesh(object: THREE.Object3D): object is THREE.Mesh {
  return object.visible && isImportedSolidBodyDisplayMesh(object);
}

/**
 * 从 mesh 材质中提取 MeshStandardMaterial 配方。
 *
 * @param mesh 导入可见实体网格。
 * @returns 提取成功时返回配方，否则返回 `null`。
 */
function extractMeshStandardMaterialRecipe(mesh: THREE.Mesh): SolidBodyDisplayMaterialRecipe | null {
  const rawMaterial = mesh.material;
  const materials = Array.isArray(rawMaterial) ? rawMaterial : [rawMaterial];

  for (const material of materials) {
    if (!(material instanceof THREE.MeshStandardMaterial)) {
      continue;
    }

    return {
      color: material.color.getHex(),
      metalness: material.metalness,
      roughness: material.roughness,
    };
  }

  return null;
}

/**
 * 从当前场景首个导入可见实体网格采样显示材质配方。
 *
 * 设计说明：
 * 1. 特征结果体应与同零件已加载的 GLB 可见网格保持 `color/metalness/roughness` 一致；
 * 2. 只采样 imported-model 语义下的真实 visible mesh，排除 pick host、overlay 与拓扑代理；
 * 3. 场景中不存在可采样导入体时返回 `null`，由调用方回退到 preset 默认配方。
 *
 * @param scene 当前 Viewer 场景。
 * @returns 采样到的材质配方；无可采样对象时返回 `null`。
 */
export function resolveImportedVisibleSolidBodyMaterialRecipe(
  scene: THREE.Scene,
): SolidBodyDisplayMaterialRecipe | null {
  let resolvedRecipe: SolidBodyDisplayMaterialRecipe | null = null;

  scene.traverse((object) => {
    if (resolvedRecipe) {
      return;
    }
    if (!isImportedVisibleSolidBodyMesh(object)) {
      return;
    }

    resolvedRecipe = extractMeshStandardMaterialRecipe(object);
  });

  return resolvedRecipe;
}
